import { useEffect, useRef, useState } from "react";

import { getCreatorChannelVideos } from "../services/creatorChannelVideosService";

const DEFAULT_LIMIT = 12;

const mergeVideos = (current, incoming) => {
  const map = new Map();

  [...current, ...incoming].forEach((video) => {
    const key = video.videoTag ?? video.videoId;

    if (key != null) {
      map.set(String(key), video);
    }
  });

  return [...map.values()];
};

export const useCreatorChannelVideos = ({
  username,
  limit = DEFAULT_LIMIT,
} = {}) => {
  const [videos, setVideos] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const controllerRef = useRef(null);
  const requestRef = useRef(0);
  const videosRef = useRef([]);
  const offsetRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingRef = useRef(false);

  const creatorUsername = String(username || "").trim();

  const parsedLimit = Math.min(
    Math.max(Number.parseInt(limit, 10) || DEFAULT_LIMIT, 1),
    30,
  );

  const fetchVideos = async (reset = false) => {
    if (!creatorUsername) return false;

    if (loadingRef.current && !reset) {
      return false;
    }

    controllerRef.current?.abort();

    const controller = new AbortController();
    const requestId = ++requestRef.current;

    controllerRef.current = controller;
    loadingRef.current = true;

    const offset = reset ? 0 : offsetRef.current;

    if (reset) {
      setLoading(true);
      setLoadingMore(false);
      setError("");
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await getCreatorChannelVideos(
        creatorUsername,
        {
          limit: parsedLimit,
          offset,
        },
        controller.signal,
      );

      if (controller.signal.aborted || requestId !== requestRef.current) {
        return false;
      }

      const data = response?.data || {};

      if (data.success === false) {
        throw new Error(data.message || "Unable to load channel videos.");
      }

      if (!Array.isArray(data.videos)) {
        throw new Error("Invalid channel videos response.");
      }

      const received = data.videos.filter(Boolean);

      const nextVideos = reset
        ? received
        : mergeVideos(videosRef.current, received);

      const nextOffset = offset + received.length;
      const pagination = data.pagination || {};

      const nextTotal = Number(pagination.total) || 0;

      const nextHasMore =
        typeof pagination.hasMore === "boolean"
          ? pagination.hasMore
          : nextOffset < nextTotal;

      videosRef.current = nextVideos;
      offsetRef.current = nextOffset;
      hasMoreRef.current = nextHasMore;

      setVideos(nextVideos);
      setTotal(nextTotal);
      setHasMore(nextHasMore);
      setError("");

      return {
        videos: nextVideos,
        pagination,
      };
    } catch (err) {
      if (controller.signal.aborted || requestId !== requestRef.current) {
        return false;
      }

      console.error("Failed to load channel videos:", err);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load channel videos.",
      );

      return false;
    } finally {
      if (requestId === requestRef.current) {
        controllerRef.current = null;
        loadingRef.current = false;

        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    controllerRef.current?.abort();
    requestRef.current += 1;

    controllerRef.current = null;
    loadingRef.current = false;
    videosRef.current = [];
    offsetRef.current = 0;
    hasMoreRef.current = false;

    setVideos([]);
    setTotal(0);
    setHasMore(false);
    setError("");

    if (creatorUsername) {
      fetchVideos(true);
    } else {
      setLoading(false);
      setLoadingMore(false);
    }

    return () => {
      requestRef.current += 1;
      controllerRef.current?.abort();
      controllerRef.current = null;
      loadingRef.current = false;
    };
  }, [creatorUsername, parsedLimit]);

  const refresh = () => fetchVideos(true);

  const loadMore = () => {
    if (!creatorUsername || loadingRef.current || !hasMoreRef.current) {
      return Promise.resolve(false);
    }

    return fetchVideos(false);
  };

  return {
    videos,
    total,
    loading,
    loadingMore,
    error,
    hasMore,
    getCreatorChannelVideos: refresh,
    refresh,
    loadMore,
  };
};

export default useCreatorChannelVideos;
